import React from 'react';

import type { ExtensionProvider } from '@atlaskit/editor-common/extensions/extension-provider';
import type {
	ExtensionComponentProps,
	ExtensionManifest,
	PreloadableExtensionModuleNode,
} from '@atlaskit/editor-common/extensions/extension-manifest';

import { ForgeMacroMeasurements } from '@confluence/forge-ui/entry-points/ForgeMacroMeasurements.ts';

type MacroRenderer = React.ComponentType<React.PropsWithChildren<ExtensionComponentProps>>;

/** Decorate resolved renderers, preserving provider lookup, preload and referentiality behavior. */
export function withForgeMacroMeasurements(
	provider: ExtensionProvider,
	isEditing: boolean,
): ExtensionProvider {
	if (isEditing) {
		return provider;
	}
	const cache = new WeakMap<ExtensionManifest, ExtensionManifest>();
	const componentCache = new WeakMap<MacroRenderer, Map<string, MacroRenderer>>();
	const wrap = (manifest: ExtensionManifest | undefined): ExtensionManifest | undefined => {
		if (!manifest?.modules.nodes) {
			return manifest;
		}
		const cached = cache.get(manifest);
		if (cached) {
			return cached;
		}
		const nodes = Object.fromEntries(
			Object.entries(manifest.modules.nodes).map(([key, module]) => {
				if (module.type !== 'extension' && module.type !== 'bodiedExtension') {
					return [key, module];
				}
				const wrapComponent = (loaded: Awaited<ReturnType<typeof module.render>>) => {
					const Component = 'default' in loaded ? loaded.default : loaded;
					// A renderer can serve both node types; preserve its manifest fallback for each.
					const renderers = componentCache.get(Component) ?? new Map<string, MacroRenderer>();
					const cachedComponent = renderers.get(module.type);
					if (cachedComponent) return cachedComponent;
					const MeasuredMacro: MacroRenderer = (props) => (
						<ForgeMacroMeasurements
							extensionKey={props.node.extensionKey}
							nodeType={props.node.type ?? module.type}
							isEditing={isEditing}
						>
							<Component {...props} />
						</ForgeMacroMeasurements>
					);
					MeasuredMacro.displayName = `ForgeMacroMeasurements(${Component.displayName || 'Macro'})`;
					renderers.set(module.type, MeasuredMacro);
					componentCache.set(Component, renderers);
					return MeasuredMacro;
				};
				return [
					key,
					{
						...module,
						render: async () => wrapComponent(await module.render()),
						...('renderSync' in module && typeof module.renderSync === 'function'
							? {
									renderSync: () =>
										wrapComponent((module as PreloadableExtensionModuleNode).renderSync()),
								}
							: {}),
					},
				];
			}),
		);
		const wrapped = { ...manifest, modules: { ...manifest.modules, nodes } };
		cache.set(manifest, wrapped);
		cache.set(wrapped, wrapped);
		return wrapped;
	};
	// Retain the provider instance and its specialized migration methods. The renderer resolves
	// manifests through these two methods, including synchronously preloaded Forge manifests.
	const getExtension = provider.getExtension.bind(provider);
	provider.getExtension = async (...args) => wrap(await getExtension(...args));
	if (provider.getPreloadedExtension) {
		const getPreloadedExtension = provider.getPreloadedExtension.bind(provider);
		provider.getPreloadedExtension = (...args) => wrap(getPreloadedExtension(...args));
	}
	return provider;
}
